import React from 'react';
import { 
  ShoppingBag, 
  Receipt, 
  BarChart3, 
  Zap, 
  Shield, 
  Cloud, 
  Brain,
  Calculator
} from 'lucide-react';
// Import SVG as path
import whatsappSvgPath from '../assets/whatsapp.svg';
import { useLanguage } from '../contexts/LanguageContext';

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="relative border-gray-300 cursor-pointer group p-8 rounded-2xl transition-all duration-500 ease-in-out hover:scale-105 border hover:border-teal-500 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D3C4C] to-[#165469] opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out"></div>
      <div className="relative z-10">
        <div className="text-teal-500 group-hover:text-white w-14 h-14 flex items-center justify-center rounded-xl group-hover:bg-teal-500/20 mb-6 transition-all duration-500 ease-in-out">
          {icon}
        </div>
        <h4 className="text-xl font-bold text-[#0D3C4C] group-hover:text-white mb-3 transition-all duration-500 ease-in-out">{title}</h4>
        <p className="text-gray-600 group-hover:text-gray-300 transition-all duration-500 ease-in-out">{description}</p>
      </div>
    </div>
  );
}

export function Features() {
  const { t } = useLanguage();

  const features = [
    { 
      icon: <Brain size={28} className="animate-pulse" />, 
      ...t.features.cards.ai,
      highlight: true 
    },
    { icon: <ShoppingBag size={28} />, ...t.features.cards.inventory },
    { icon: <Receipt size={28} />, ...t.features.cards.printing },
    { icon: <BarChart3 size={28} />, ...t.features.cards.analytics },
    { icon: <Zap size={28} />, ...t.features.cards.performance },
    { icon: <Shield size={28} />, ...t.features.cards.security },
    { icon: <Cloud size={28} />, ...t.features.cards.backup },
    { icon: <div className="w-7 h-7" style={{ mask: `url(${whatsappSvgPath}) no-repeat center / contain`, WebkitMask: `url(${whatsappSvgPath}) no-repeat center / contain`, backgroundColor: 'currentColor' }} />, ...t.features.cards.whatsapp },
    { icon: <Calculator size={28} />, ...t.features.cards.accounting }
  ];

  return (
    <section id="features" className="py-32 bg-slate-100">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h3 className="text-4xl font-bold text-[#0D3C4C] mb-6">
            {t.features.title}
          </h3>
          <p className="text-xl text-gray-600">
            {t.features.subtitle}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard 
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
}